import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { getTokenAndId } from "utils/helper/localStorage";
import { store } from "stores/store";
import { parseIfJson } from "utils/helper/json";
import { useAuthenticate } from "./useAuthenticate";

const host = API_DOMAIN === "" ? location.host : API_DOMAIN;

const BASE_URL = `${IS_PROXY ? "http" : "https"}://${host}`;

const getApiInfo = (params?: any) => {
  let token = "";
  let id = "";

  if (params) {
    if (Object.prototype.hasOwnProperty.call(params, "tokenKey"))
      token = params["tokenKey"];
    if (Object.prototype.hasOwnProperty.call(params, "userId"))
      id = params["userId"];
  }

  if (token === "" || id === "") {
    const tokenAndId = getTokenAndId();
    token = tokenAndId.token;
    id = tokenAndId.id;
  }

  return { token, id };
};

const fetcher = async (
  path: string,
  params: any,
  { token, id }: { token: string; id: string }
) => {
  const headers: Record<string, string> = {};

  if (token) {
    headers["x-nvs-token"] = token;
  }

  store.dispatch({ type: `api${path}_PENDING` });

  const response = await fetch(
    `${BASE_URL}/${API_PATH}${path}${
      params ? `?${new URLSearchParams(params)}` : ""
    }${id ? `${params ? "&" : "?"}userId=${id}` : ""}`,
    { headers }
  );

  const json = await response.json();

  if (!response.ok) {
    const vsError = handleError({ status: response.status, data: json }, path);

    const error = {
      status: response.status,
      data: json,
      message: vsError.message
    };

    store.dispatch({ type: `api${path}_REJECTED`, payload: vsError });

    throw error;
  }

  store.dispatch({
    type: `api${path}_FULFILLED`,
    payload: json
  });

  return json;
};

// データ取得のフック
export const useRequest = <R>(path: string, params?: any) => {
  const { token, id } = getApiInfo(params);
  const { needAuthenticate } = useAuthenticate();

  const { data, error, isLoading } = useSWR<R>(
    [path, params, { token, id }],
    ([url, params, { token, id }]: [
      string,
      any,
      { token: string; id: string }
    ]) => fetcher(url, params, { token, id }),
    {
      dedupingInterval: 0,
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      onError: error => {
        if ((error as { data?: { code?: number } }).data?.code === 401) {
          needAuthenticate();
        }
      }
    }
  );

  return {
    data,
    error,
    isLoading
  };
};

class VsApiError implements Error {
  public name = "VsApiError";
  public body: any;
  public path = "";

  constructor(public message: string) {}

  toString() {
    return `${this.name} ${this.message}`;
  }
}

const handleError = (error: { status: number; data: any }, path: string) => {
  let message = "UNHANDLED_ERROR";

  switch (error.status) {
    case 400:
      message = "BAD_REQUEST";
      break;
    case 401:
      message = "UNAUTHORIZED";
      break;
    case 500:
      message = "INTERNAL_SERVER_ERROR";
      break;
    case 502:
      message = "BAD_GATEWAY";
      break;
    case 504:
      message = "GATEWAY_TIMEOUT";
      break;
    case 404:
      message = "NOT_FOUND";
      break;
  }

  const vsError = new VsApiError(message);
  if (error.data) {
    vsError.body = error.data;
  }
  vsError.path = path;

  return vsError;
};

type MutationKey = {
  path: string;
};

const mutationFetcher = async (
  key: MutationKey,
  {
    arg,
    method = "POST",
    headers: customHeaders = {}
  }: {
    arg?: any;
    method?: string;
    headers?: Record<string, string>;
  }
) => {
  const { path } = key;
  const { token, id } = getApiInfo(arg);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...customHeaders
  };

  if (token) {
    headers["x-nvs-token"] = token;
  }

  store.dispatch({ type: `api${path}_PENDING` });

  const requestParams = { ...arg, userId: id };

  const response = await fetch(
    `${BASE_URL}/${API_PATH}${path}${
      requestParams && method === "GET"
        ? `?${new URLSearchParams(requestParams)}`
        : ""
    }`,
    {
      method,
      headers,
      body: method === "POST" ? JSON.stringify(requestParams) : undefined
    }
  );

  const responseData = await response.json();

  if (!response.ok) {
    const vsError = handleError(
      { status: response.status, data: responseData },
      path
    );

    const error = {
      status: response.status,
      data: responseData,
      message: vsError.message
    };

    store.dispatch({ type: `api${path}_REJECTED`, payload: vsError });

    throw error;
  }

  store.dispatch({ type: `api${path}_FULFILLED`, payload: responseData });

  return responseData;
};

/**
 * トリガー型のデータ取得フック
 * @template R 取得データの型
 * @template P トリガーに渡すパラメータの型
 * @template E エラーの型
 */
export const useRequestMutation = <R = unknown, P = unknown, E = unknown>(
  path: string,
  method: "GET" | "POST" = "POST",
  options?: {
    onSuccess?: (data: R) => void;
    onError?: (error: E) => void;
  }
) => {
  const { needAuthenticate } = useAuthenticate();
  const swrKey: MutationKey = {
    path
  };

  const { trigger, isMutating, data, error } = useSWRMutation<
    R,
    E,
    MutationKey,
    P
  >(
    swrKey,
    (key: MutationKey, { arg }: { arg: P }) =>
      mutationFetcher(key, { arg, method }),
    {
      onSuccess: options?.onSuccess,
      onError: error => {
        if ((error as { data?: { code?: number } }).data?.code === 401) {
          needAuthenticate();
        }

        options?.onError?.(error);
      },
      populateCache: false,
      revalidate: false
    }
  );

  return {
    trigger,
    isMutating,
    data,
    error
  };
};

interface SseEvent<T = unknown> {
  event?: string;
  id?: string;
  data: T;
}

export type SseStatus = "idle" | "connecting" | "open" | "error" | "closed";
// SSEのクローズ理由
export type SseCloseReason = "server" | "manual" | "unmount";

interface SseStatusChangeMeta {
  closeReason?: SseCloseReason;
}

// SSEエラー構築
const buildSseError = async (res: Response, path: string) => {
  let data: unknown = undefined;

  try {
    const text = await res.text();
    data = text ? parseIfJson(text) : undefined;
  } catch (e) {
    data = undefined;
  }

  const vsError = handleError({ status: res.status, data }, path);

  return {
    status: res.status,
    data,
    message: vsError.message
  };
};

// SSE対応のPOSTリクエストフック
export function usePostSse<T>(
  url: string,
  options?: {
    headers?: HeadersInit;
    onEvent?: (e: SseEvent<T>) => void;
    onStatusChange?: (
      status: SseStatus,
      prevStatus?: SseStatus,
      meta?: SseStatusChangeMeta
    ) => void;
    onError?: (error: unknown) => void;
  }
) {
  const [status, setStatus] = useState<SseStatus>("idle");
  const abortRef = useRef<AbortController | null>(null);
  const optionsRef = useRef<typeof options>(options);
  const { needAuthenticate } = useAuthenticate();

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const setStatusWithEvent = useCallback(
    (nextStatus: SseStatus, meta?: SseStatusChangeMeta) => {
      setStatus(prevStatus => {
        if (prevStatus === nextStatus) return prevStatus;
        optionsRef.current?.onStatusChange?.(nextStatus, prevStatus, meta);
        return nextStatus;
      });
    },
    []
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      setStatusWithEvent("closed", { closeReason: "unmount" });
    };
  }, [setStatusWithEvent]);

  const trigger = useCallback(
    (requestBody: object) => {
      abortRef.current?.abort();

      const abortController = new AbortController();
      abortRef.current = abortController;
      setStatusWithEvent("connecting");

      (async () => {
        try {
          const { token, id } = getApiInfo(requestBody);

          // SSEリクエストの送信
          const res = await fetch(`${BASE_URL}/stream${url}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-nvs-token": token,
              ...optionsRef.current?.headers
            },
            body: JSON.stringify({ ...requestBody, userId: id }),
            signal: abortController.signal
          });

          // エラーハンドリング
          if (!res.ok || !res.body) {
            const error = await buildSseError(res, `/stream${url}`);

            if (error.status === 401) {
              needAuthenticate();
            }

            throw error;
          }

          setStatusWithEvent("open");

          const reader = res.body.getReader();
          const decoder = new TextDecoder("utf-8");

          let buffer = "";
          let eventName: string | undefined;
          let eventId: string | undefined;
          let dataLines: string[] = [];

          // 1イベント分の状態を初期化
          const resetEvent = () => {
            eventName = undefined;
            eventId = undefined;
            dataLines = [];
          };

          // dataが揃ったらイベントを発火
          const emit = () => {
            if (!dataLines.length) return;

            const data = parseIfJson(dataLines.join("\n"));

            optionsRef.current?.onEvent?.({
              event: eventName,
              id: eventId,
              data
            });
            resetEvent();
          };

          // SSEの1行を解釈して状態に変数に追加
          const handleLine = (line: string) => {
            if (line === "") return emit();
            if (line.startsWith(":")) return;

            const [field, ...rest] = line.split(":");
            const value = rest.join(":").trimStart();

            if (field === "event") eventName = value.trim();
            else if (field === "id") eventId = value;
            else if (field === "data") dataLines.push(value);
          };

          while (true) {
            // ストリームの読み込み
            const { value, done } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";
            for (const rawLine of lines) {
              handleLine(rawLine.replace(/\r$/, ""));
            }
          }

          setStatusWithEvent("closed", { closeReason: "server" });
        } catch (e) {
          if (!abortController.signal.aborted) {
            console.error(e);
            optionsRef.current?.onError?.(e);
            setStatusWithEvent("error");
          }
        }
      })();
    },
    [needAuthenticate, setStatusWithEvent, url]
  );

  return {
    status,
    trigger,
    close: () => {
      abortRef.current?.abort();
      setStatusWithEvent("closed", { closeReason: "manual" });
    }
  };
}

/**
 * 同じパスで複数回リクエストするためのフック
 */
export const useRequestMutationMultiple = <R = unknown, P = unknown>(
  path: string,
  method: "GET" | "POST" = "GET",
  options?: {
    onSuccess?: (data: R) => void;
    onError?: (error: any) => void;
  }
) => {
  const [data, setData] = useState<R[]>([]);
  const swrKey: MutationKey = {
    path
  };

  const { trigger: mutationTrigger } = useSWRMutation<R, any, any, any>(
    swrKey,
    (key: MutationKey, { arg }: { arg: P }) =>
      mutationFetcher(key, { arg, method }),
    {
      populateCache: false,
      revalidate: false
    }
  );

  const trigger = async (paramsArray: P[] = []): Promise<R[]> => {
    if (paramsArray.length === 0) {
      return [];
    }

    paramsArray.forEach(async params => {
      try {
        const result = await mutationTrigger(params);
        setData(prevData => [...prevData, result]);
        if (options?.onSuccess) {
          options.onSuccess(result);
        }
      } catch (error) {
        if (options?.onError) {
          options.onError(error);
        }
      }
    });

    return data;
  };

  const clearData = () => {
    setData([]);
  };

  return { data, trigger, clearData };
};
